import { yjsInstance } from '../yjsInstance.mjs'
import { nanoid } from 'nanoid'

export function migrateYDocXmlFragment(node) {
  let migrate1 = migrateV1(node)
  let migrate2 = migrateV2(node)
  return migrate1 || migrate2
}

/**
 * Migrates a Yjs XmlFragment node from version 0 to version 1, wherein we replace
 * bulletList and orderedList nodes with customListItem nodes.
 *
 * @param node
 * @return {boolean}
 */
function migrateV1(node) {
  let changed = false
  let nChildren = node.length || 0
  for (let i = nChildren - 1; i >= 0; i--) {
    const child = node.get(i)
    if (
      child instanceof yjsInstance.Y.XmlElement &&
      (child.nodeName === 'bulletList' || child.nodeName === 'orderedList')
    ) {
      changed = true
      const customListItems = processList(child)
      node.delete(i, 1)
      node.insert(i, customListItems)
    }
  }
  return changed
}

/**
 * Migrates a Yjs XmlFragment node from version 1 to version 2, wherein we
 * ensure that customListItem nodes are properly formatted with list IDs and
 * item numbers.
 *
 * This migration ensures that contiguous customListItem nodes are grouped
 * together and assigned a unique list ID.
 *
 * @param node
 * @return {boolean}
 */
function migrateV2(node) {
  const listableNodeTypes = ['noteBoardNote']
  let changed = false
  // 1. Contiguous list items should be assigned a random group ID via a `list-id` attribute.
  // 2. Bullet lists/ordered lists should be adjusted to use unordered1/ordered5 instead of unordered-standard/ordered-standard.
  const contiguousLists = []
  let currentList = null
  let nChildren = node.length || 0
  const styleTypeTranslationMap = {
    'unordered-standard': 'unordered1',
    'ordered-standard': 'ordered5',
  }
  for (let i = nChildren - 1; i >= 0; i--) {
    const child = node.get(i)
    if (
      child instanceof yjsInstance.Y.XmlElement &&
      child.nodeName === 'customListItem'
    ) {
      if (!currentList) {
        currentList = []
        contiguousLists.push(currentList)
      }
      if (styleTypeTranslationMap[child.getAttribute('list-style-type')]) {
        child.setAttribute(
          'list-style-type',
          styleTypeTranslationMap[child.getAttribute('list-style-type')],
        )
        changed = true
      }
      currentList.push(child)
    } else if (listableNodeTypes.includes(child.nodeName)) {
      if (currentList) currentList.push(child)
    } else {
      // Otherwise, the contiguity is broken.
      currentList = null
    }
  }

  // Now we have contiguous lists in contiguousLists.  Each of them needs a random ID.
  for (const list of contiguousLists) {
    if (list.length > 0) {
      const listId = nanoid()
      let itemNo = 0
      for (const item of list) {
        if (
          item.nodeName === 'customListItem' &&
          !item.getAttribute('list-id')
        ) {
          item.setAttribute('item-number', itemNo)
          itemNo++
          item.setAttribute('list-id', listId)
          changed = true
        }
      }
    }
  }

  return changed
}

function processList(node, depth = 0) {
  const result = []
  const listableNodeTypes = ['noteBoardNote']
  let nChildren = node.length || 0
  for (let i = 0; i < nChildren; i++) {
    const child = node.get(i)
    if (
      !(child instanceof yjsInstance.Y.XmlElement) ||
      child.nodeName !== 'listItem'
    ) {
      console.warn(
        `Unexpected ${child.nodeName} in list, expected 'listItem', ignoring it.`,
      )
      continue
    }
    const grandChildren = child.length || 0
    for (let j = 0; j < grandChildren; j++) {
      const grandChild = child.get(j)
      if (!(grandChild instanceof yjsInstance.Y.XmlElement)) {
        continue
      }
      if (grandChild.nodeName === 'paragraph') {
        // Create a new customlistitem node with the paragraph as a child
        const newNode = new yjsInstance.Y.XmlElement('customListItem')
        newNode.setAttribute('depth', depth)
        newNode.setAttribute(
          'list-style-type',
          node.nodeName === 'bulletList' ? 'unordered1' : 'ordered5',
        )
        newNode.setAttribute('item-number', i)
        newNode.insert(0, [grandChild.clone()])
        result.push(newNode)
      } else if (listableNodeTypes.includes(grandChild.nodeName)) {
        result.push(grandChild.clone())
      } else if (
        grandChild.nodeName === 'bulletList' ||
        grandChild.nodeName === 'orderedList'
      ) {
        result.push(...processList(grandChild, depth + 1))
      }
    }
  }
  return result
}
